// components/MemberPortal/Setup.js
import React, { useState } from 'react';
import axiosInstance from '../../axiosConfig';
import VerificationMethodSelection from './VerificationMethodSelection';
import './MemberPortal.css';

const MemberPortalSetup = ({ onProfileCreated }) => {
  const [step, setStep] = useState(1);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubSearch, setClubSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    handicap: '',
    member_since: '',
    bio: '',
    preferred_days: '',
    preferred_tee_time: '',
    playing_level: ''
  });

  const searchClubs = async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axiosInstance.get(`/member-portal/clubs/search/?search=${query}`);
      setSearchResults(response.data.clubs || []);
    } catch (error) {
      console.error('Club search error:', error);
      setSearchResults([]);
    }
  };

  const handleClubSelect = (club) => {
    setSelectedClub(club);
    setClubSearch(club.name);
    setSearchResults([]);
  };

  const handleSubmitProfile = async () => {
    if (!selectedClub) return;

    setLoading(true);
    try {
      const payload = {
        ...profileData,
        home_club_id: selectedClub.club_id
      };

      await axiosInstance.post('/member-portal/setup/', payload);
      setStep(2);
    } catch (error) {
      console.error('Profile setup error:', error);
      alert('Error setting up profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (step === 1) {
    return (
      <div className="member-portal-setup">
        <div className="setup-header">
          <h2>Join the Member Portal</h2>
          <p>Connect with verified club members nationwide to schedule rounds</p>
        </div>

        <div className="setup-form">
          <div className="form-section">
            <label>Your Home Club *</label>
            <div className="club-search-container">
              <input
                type="text"
                placeholder="Search for your club..."
                value={clubSearch}
                onChange={(e) => {
                  setClubSearch(e.target.value);
                  searchClubs(e.target.value);
                }}
                className="club-search-input"
              />

              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((club) => (
                    <div
                      key={club.club_id}
                      className="search-result-item"
                      onClick={() => handleClubSelect(club)}
                    >
                      <div className="club-name">{club.name}</div>
                      <div className="club-location">{club.city}, {club.state}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedClub && (
            <div className="selected-club">
              <h3>Selected Club</h3>
              <div className="club-card">
                <h4>{selectedClub.name}</h4>
                <p>{selectedClub.address}</p>
                <p>{selectedClub.city}, {selectedClub.state} {selectedClub.zip_code}</p>
              </div>
            </div>
          )}

          <div className="form-section">
            <label>Handicap</label>
            <input
              type="number"
              step="0.1"
              value={profileData.handicap}
              onChange={(e) => setProfileData({...profileData, handicap: e.target.value})}
              placeholder="e.g., 12.5"
            />
          </div>

          <div className="form-section">
            <label>Club Member Since</label>
            <input
              type="date"
              value={profileData.member_since}
              onChange={(e) => setProfileData({...profileData, member_since: e.target.value})}
            />
          </div>

          <div className="form-section">
            <label>Brief Bio</label>
            <textarea
              placeholder="Tell other members about yourself..."
              value={profileData.bio}
              onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
              rows={3}
            />
          </div>

          <div className="form-section">
            <label>Preferred Playing Days</label>
            <input
              placeholder="e.g., Weekends, Friday afternoons"
              value={profileData.preferred_days}
              onChange={(e) => setProfileData({...profileData, preferred_days: e.target.value})}
            />
          </div>

          <div className="form-section">
            <label>Preferred Tee Times</label>
            <select
              value={profileData.preferred_tee_time}
              onChange={(e) => setProfileData({...profileData, preferred_tee_time: e.target.value})}
            >
              <option value="">Select preference</option>
              <option value="Early Morning (7-9 AM)">Early Morning (7-9 AM)</option>
              <option value="Morning (9-11 AM)">Morning (9-11 AM)</option>
              <option value="Afternoon (1-3 PM)">Afternoon (1-3 PM)</option>
              <option value="Late Afternoon (3-5 PM)">Late Afternoon (3-5 PM)</option>
              <option value="Flexible">Flexible</option>
            </select>
          </div>

          <div className="form-section">
            <label>Playing Level</label>
            <select
              value={profileData.playing_level}
              onChange={(e) => setProfileData({...profileData, playing_level: e.target.value})}
            >
              <option value="">Select level</option>
              <option value="Casual">Casual</option>
              <option value="Competitive">Competitive</option>
              <option value="Tournament Player">Tournament Player</option>
            </select>
          </div>

          <button
            className="continue-button"
            onClick={handleSubmitProfile}
            disabled={!selectedClub || loading}
          >
            {loading ? 'Setting up...' : 'Continue to Verification'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 2) {
    return <VerificationMethodSelection onComplete={onProfileCreated} />;
  }

  return null;
};

export default MemberPortalSetup;